import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Train, ArrowLeftRight, Clock, IndianRupee, MapPin, Search,
  Loader2, AlertCircle, Minus, Plus, Coffee, Navigation,
} from 'lucide-react';
import StationAutocomplete from '../components/StationAutocomplete';
import { Input } from '../components/ui/input';

import { API } from '../config';

const TABS = [
  { id: 'between', label: 'Trains Between Stations', short: 'Trains', icon: ArrowLeftRight },
  { id: 'fare', label: 'Fare Calculator', short: 'Fare', icon: IndianRupee },
  { id: 'timetable', label: 'Train Time Table', short: 'Time Table', icon: Clock },
];

const Spinner = () => (
  <div className="flex flex-col items-center justify-center py-12 text-tp-violet" data-testid="loading">
    <Loader2 className="animate-spin" size={28} />
    <span className="mt-2 text-sm text-gray-500">Fetching latest data…</span>
  </div>
);

const ErrorBox = ({ msg }) => (
  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-700" data-testid="error-box">
    <AlertCircle size={18} className="shrink-0 mt-0.5" /> <span>{msg}</span>
  </div>
);

const DayChips = ({ days, daily }) => (
  <div className="flex flex-wrap gap-1">
    {daily
      ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-tp-lime-soft text-tp-lime-deep">Runs Daily</span>
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
        <span key={d} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${days.includes(d) ? 'bg-tp-violet text-white' : 'bg-gray-100 text-gray-400'}`}>{d}</span>
      ))}
  </div>
);

// ---------------- Trains Between Stations ----------------
const BetweenStations = () => {
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [res, setRes] = useState(null);

  const swap = () => { setFrom(to); setTo(from); };

  const search = async () => {
    if (!from || !to) { setErr('Please select both source and destination stations.'); return; }
    if (from.code === to.code) { setErr('Source and destination cannot be the same.'); return; }
    setErr(''); setLoading(true); setRes(null);
    try {
      const r = await axios.get(`${API}/trains/between`, { params: { from_code: from.code, to_code: to.code } });
      setRes(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not fetch trains. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <StationAutocomplete value={from} onChange={setFrom} placeholder="From station" testid="bwn-from" />
        <button onClick={swap} data-testid="bwn-swap" className="mx-auto w-10 h-10 rounded-full bg-tp-violet-soft text-tp-violet flex items-center justify-center hover:bg-tp-violet hover:text-white transition-colors rotate-90 md:rotate-0">
          <ArrowLeftRight size={18} />
        </button>
        <StationAutocomplete value={to} onChange={setTo} placeholder="To station" testid="bwn-to" />
      </div>
      <button onClick={search} data-testid="bwn-search" className="w-full btn-primary rounded-2xl h-12 flex items-center justify-center gap-2 font-bold">
        <Search size={16} /> Find Trains
      </button>

      {err && <ErrorBox msg={err} />}
      {loading && <Spinner />}

      {res && !loading && (
        <div data-testid="bwn-results">
          <div className="text-sm text-gray-500 mb-3">
            <span className="font-bold text-tp-ink">{res.count}</span> trains from{' '}
            <span className="font-bold text-tp-violet">{res.from.name}</span> to{' '}
            <span className="font-bold text-tp-violet">{res.to.name}</span>
          </div>
          {res.count === 0 && <ErrorBox msg="No direct trains found between these stations." />}
          <div className="space-y-3">
            {res.trains.map((t) => (
              <div key={t.trainNumber} className="bg-white rounded-2xl border border-tp-violet/10 p-4 hover:border-tp-violet/40 transition-colors" data-testid={`train-${t.trainNumber}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-extrabold text-tp-ink flex items-center gap-2">
                      <span className="text-tp-violet">{t.trainNumber}</span> {t.trainName}
                      {t.hasPantry && <Coffee size={14} className="text-tp-lime-deep" title="Pantry available" />}
                    </div>
                    <DayChips days={t.runningDays} daily={t.runsDaily} />
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-gray-400 font-semibold">{t.distance} km · {t.duration}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3 text-tp-ink">
                  <div className="text-center">
                    <div className="text-lg font-black leading-tight">{t.departureTime}</div>
                    <div className="text-[11px] text-gray-400 font-semibold">{t.fromCode}</div>
                  </div>
                  <div className="flex-1 flex items-center gap-1 text-gray-300">
                    <div className="h-px flex-1 bg-gray-200" />
                    <Train size={14} className="text-tp-violet" />
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-black leading-tight">{t.arrivalTime}</div>
                    <div className="text-[11px] text-gray-400 font-semibold">{t.toCode}</div>
                  </div>
                </div>
                {(t.classes?.length > 0) && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-50">
                    {t.classes.map((c) => (
                      <span key={c} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-tp-violet-soft text-tp-violet-deep">
                        {c}{t.fares?.[c] ? ` · ₹${t.fares[c]}` : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Fare Calculator ----------------
const FareCalculator = () => {
  const [train, setTrain] = useState('');
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [res, setRes] = useState(null);

  const calc = async () => {
    if (!train.trim() || !from || !to) { setErr('Enter train number and select both stations.'); return; }
    setErr(''); setLoading(true); setRes(null);
    try {
      const r = await axios.get(`${API}/trains/fare`, { params: { train: train.trim(), from_code: from.code, to_code: to.code } });
      setRes(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not calculate fare. Please try again.'); }
    setLoading(false);
  };

  const pax = adults + children; // children 5-11 charged full fare (current IR rule)

  const Stepper = ({ label, sub, val, set }) => (
    <div className="flex items-center justify-between bg-white border-2 border-gray-100 rounded-2xl px-4 py-2.5">
      <div><div className="text-sm font-bold text-tp-ink">{label}</div><div className="text-[11px] text-gray-400">{sub}</div></div>
      <div className="flex items-center gap-3">
        <button onClick={() => set(Math.max(label === 'Adults' ? 1 : 0, val - 1))} data-testid={`fare-${label.toLowerCase()}-minus`} className="w-8 h-8 rounded-full bg-tp-violet-soft text-tp-violet flex items-center justify-center"><Minus size={14} /></button>
        <span className="w-6 text-center font-extrabold text-tp-ink" data-testid={`fare-${label.toLowerCase()}-val`}>{val}</span>
        <button onClick={() => set(Math.min(6, val + 1))} data-testid={`fare-${label.toLowerCase()}-plus`} className="w-8 h-8 rounded-full bg-tp-violet text-white flex items-center justify-center"><Plus size={14} /></button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-2 border-gray-100 rounded-2xl focus-within:border-tp-violet px-3 transition-colors bg-white">
        <Train size={18} className="text-tp-violet" />
        <Input value={train} onChange={(e) => setTrain(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="Train number e.g. 12951" className="border-0 focus-visible:ring-0 h-11 text-base" data-testid="fare-train" />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <StationAutocomplete value={from} onChange={setFrom} placeholder="From station" testid="fare-from" />
        <StationAutocomplete value={to} onChange={setTo} placeholder="To station" testid="fare-to" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stepper label="Adults" sub="12+ yrs" val={adults} set={setAdults} />
        <Stepper label="Children" sub="5-11 yrs" val={children} set={setChildren} />
      </div>
      <button onClick={calc} data-testid="fare-calc" className="w-full btn-primary rounded-2xl h-12 flex items-center justify-center gap-2 font-bold">
        <IndianRupee size={16} /> Calculate Fare
      </button>

      {err && <ErrorBox msg={err} />}
      {loading && <Spinner />}

      {res && !loading && (
        <div data-testid="fare-results" className="bg-white rounded-2xl border border-tp-violet/10 p-4">
          <div className="font-extrabold text-tp-ink"><span className="text-tp-violet">{res.trainNumber}</span> {res.trainName}</div>
          <div className="text-xs text-gray-400 mt-0.5">{res.from.name} → {res.to.name} · {res.distance} km · {res.duration}</div>
          <div className="mt-4 space-y-2">
            {res.fares.map((f) => (
              <div key={f.classCode} className="flex items-center justify-between bg-tp-violet-soft/40 rounded-xl px-3 py-2.5" data-testid={`fare-row-${f.classCode}`}>
                <div className="text-sm font-bold text-tp-ink">{f.className}</div>
                <div className="text-right">
                  <div className="text-base font-black text-tp-violet-deep">₹{f.fare * pax}</div>
                  <div className="text-[10px] text-gray-400">₹{f.fare} × {pax} pax</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-gray-400 mt-3 flex items-center gap-1.5">
            <AlertCircle size={12} /> General quota fares. Children under 5 yrs travel free (not counted).
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Train Time Table ----------------
const TimeTable = () => {
  const [train, setTrain] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [res, setRes] = useState(null);

  const load = async () => {
    if (!train.trim()) { setErr('Enter a train number.'); return; }
    setErr(''); setLoading(true); setRes(null);
    try {
      const r = await axios.get(`${API}/trains/timetable/${train.trim()}`);
      setRes(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Time table not found for this train.'); }
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-2 border-gray-100 rounded-2xl focus-within:border-tp-violet px-3 transition-colors bg-white">
        <Train size={18} className="text-tp-violet" />
        <Input value={train} onChange={(e) => setTrain(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="Train number e.g. 12951" className="border-0 focus-visible:ring-0 h-11 text-base" data-testid="tt-train" />
      </div>
      <button onClick={load} data-testid="tt-search" className="w-full btn-primary rounded-2xl h-12 flex items-center justify-center gap-2 font-bold">
        <Clock size={16} /> Get Time Table
      </button>

      {err && <ErrorBox msg={err} />}
      {loading && <Spinner />}

      {res && !loading && (
        <div data-testid="tt-results" className="bg-white rounded-2xl border border-tp-violet/10 overflow-hidden">
          <div className="p-4 bg-gradient-violet text-white">
            <div className="font-extrabold text-lg">{res.trainNumber} {res.trainName}</div>
            <div className="text-sm text-white/85 flex items-center gap-2 mt-0.5">
              <MapPin size={13} /> {res.from} → {res.to} · {res.totalDistance} km
            </div>
          </div>
          {(() => {
            const hasPlatform = res.schedule.some((s) => s.platform && s.platform > 0);
            return (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-gray-400 border-b border-gray-100">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Station</th>
                    <th className="px-3 py-2">Arr</th>
                    <th className="px-3 py-2">Dep</th>
                    <th className="px-3 py-2">Halt</th>
                    {hasPlatform && <th className="px-3 py-2">PF</th>}
                    <th className="px-3 py-2">Day</th>
                    <th className="px-3 py-2 text-right">KM</th>
                  </tr>
                </thead>
                <tbody>
                  {res.schedule.map((s, i) => (
                    <tr key={`${s.stationCode}-${i}`} className="border-b border-gray-50 last:border-0" data-testid={`tt-stop-${i}`}>
                      <td className="px-3 py-2.5 text-gray-400">{i + 1}</td>
                      <td className="px-3 py-2.5">
                        <div className="font-bold text-tp-ink flex items-center gap-1.5">
                          {(i === 0 || i === res.schedule.length - 1) && <Navigation size={12} className="text-tp-violet" />}
                          {s.stationName}
                        </div>
                        <div className="text-[11px] text-gray-400">{s.stationCode}</div>
                      </td>
                      <td className="px-3 py-2.5 font-semibold">{s.arrival}</td>
                      <td className="px-3 py-2.5 font-semibold">{s.departure}</td>
                      <td className="px-3 py-2.5 text-gray-500">{s.halt || '—'}</td>
                      {hasPlatform && <td className="px-3 py-2.5 text-gray-500">{s.platform > 0 ? s.platform : '—'}</td>}
                      <td className="px-3 py-2.5 text-gray-500">{s.day}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{s.distance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

const TAB_THEME = {
  between: { grad: 'from-violet-500 to-indigo-600', soft: 'bg-tp-violet-soft', text: 'text-tp-violet-deep', ring: 'ring-tp-violet' },
  fare: { grad: 'from-emerald-500 to-teal-600', soft: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-500' },
  timetable: { grad: 'from-orange-500 to-rose-500', soft: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-500' },
};

const TrainTools = () => {
  const [params, setParams] = useSearchParams();
  const initial = TABS.find((t) => t.id === params.get('tab'))?.id || 'between';
  const [tab, setTab] = useState(initial);

  useEffect(() => { const t = params.get('tab'); if (t && TABS.find((x) => x.id === t)) setTab(t); }, [params]);
  const changeTab = (id) => { setTab(id); setParams({ tab: id }); };

  return (
    <div className="pb-10">
      {/* Hero header */}
      <section className="max-w-4xl mx-auto px-3 md:px-4 pt-3 md:pt-4">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-indigo-600 to-violet-700 p-4 md:p-6 text-white shadow-xl shadow-violet-500/20">
          <div className="absolute -right-6 -top-6 opacity-20 text-[100px] leading-none select-none">🚆</div>
          <div className="relative z-10">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-white/15 backdrop-blur px-2.5 py-0.5 rounded-full">
              <Train size={12} /> Train Tools
            </span>
            <h1 className="text-xl md:text-3xl font-extrabold mt-1.5 leading-tight">Everything about your train, in one place</h1>
            <p className="text-xs md:text-sm text-white/80 mt-1 max-w-lg">Find trains between stations, calculate exact fares & view the full time table with live platforms.</p>
          </div>
        </div>

        {/* Modern tab cards */}
        <div className="grid grid-cols-3 gap-2.5 md:gap-4 mt-3 md:mt-4">
          {TABS.map((t) => {
            const active = tab === t.id;
            const theme = TAB_THEME[t.id];
            return (
              <button
                key={t.id}
                onClick={() => changeTab(t.id)}
                data-testid={`tools-tab-${t.id}`}
                className={`group relative overflow-hidden rounded-2xl border p-3 md:p-4 text-left transition-all duration-300 ${
                  active
                    ? 'border-transparent shadow-lg -translate-y-0.5'
                    : 'border-gray-200 bg-white hover:border-transparent hover:shadow-md hover:-translate-y-0.5'
                }`}
              >
                {active && <div className={`absolute inset-0 bg-gradient-to-br ${theme.grad}`} />}
                <div className="relative z-10 flex flex-col items-start gap-2">
                  <div className={`w-9 h-9 md:w-11 md:h-11 rounded-xl flex items-center justify-center transition-colors ${
                    active ? 'bg-white/20 text-white' : `${theme.soft} ${theme.text} group-hover:scale-110`
                  } transition-transform`}>
                    <t.icon size={18} />
                  </div>
                  <div className={`text-[11px] md:text-sm font-bold leading-tight ${active ? 'text-white' : 'text-tp-ink'}`}>
                    <span className="hidden sm:inline">{t.label}</span>
                    <span className="sm:hidden">{t.short}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Content card */}
        <div className="bg-white rounded-3xl card-shadow border border-tp-violet/10 mt-4 p-4 md:p-6">
          {tab === 'between' && <BetweenStations />}
          {tab === 'fare' && <FareCalculator />}
          {tab === 'timetable' && <TimeTable />}
        </div>
      </section>
    </div>
  );
};

export default TrainTools;
